import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Checkbox } from "../components/ui/checkbox";
import {
  Ticket,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Percent,
  DollarSign,
  Calendar,
  Users,
  Globe,
  Search,
  CheckCircle,
  XCircle,
  Copy,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import axios from "axios";
import AdminLayout from "../components/AdminLayout";
import EditorialPageHeader from "../components/EditorialPageHeader";
import { Badge } from '../components/ui/badge';
import { Eye, Loader2 as UsageLoader } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const EmbeddedLayout = ({ children }) => <>{children}</>;

const AdminPromoCodes = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedLayout : AdminLayout;
  const { token, user } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPlatformCodes, setShowPlatformCodes] = useState(true);

  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    program_ids: [],
    max_uses: "",
    max_uses_per_user: "1",
    min_purchase_amount: "",
    expiration_date: "",
    description: "",
  });

  useEffect(() => {
    fetchPromoCodes();
    fetchPrograms();
  }, []);

  const fetchPromoCodes = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await axios.get(`${API}/promo-codes?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPromoCodes(res.data);
    } catch (error) {
      setLoadError(error.response?.data?.detail || "We couldn't load promotion codes. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const res = await axios.get(`${API}/programs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPrograms(res.data.items || res.data || []);
    } catch (error) {
      console.error("Failed to load programs");
    }
  };

  const handleSubmit = async () => {
    if (!formData.code) {
      toast.error("Promo code is required");
      return;
    }
    if (!formData.discount_value || parseFloat(formData.discount_value) <= 0) {
      toast.error("Valid discount value is required");
      return;
    }

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        scope: "org",
        org_id: user.org_id,
        program_ids: formData.program_ids.length > 0 ? formData.program_ids : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user
          ? parseInt(formData.max_uses_per_user)
          : 1,
        min_purchase_amount: formData.min_purchase_amount
          ? parseFloat(formData.min_purchase_amount)
          : null,
        expiration_date: formData.expiration_date || null,
        description: formData.description || null,
      };

      if (editingCode) {
        await axios.put(`${API}/promo-codes/${editingCode.code_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Promo code updated!");
      } else {
        await axios.post(`${API}/promo-codes`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Promo code created!");
      }

      setDialogOpen(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save promo code");
    }
  };

  const handleDelete = async (codeId) => {
    if (!window.confirm("Are you sure you want to deactivate this promo code?")) return;

    try {
      await axios.delete(`${API}/promo-codes/${codeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Promo code deactivated");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await axios.put(
        `${API}/promo-codes/${code.code_id}`,
        { is_active: !code.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(code.is_active ? "Promo code deactivated" : "Promo code activated");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const openEditDialog = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value.toString(),
      program_ids: code.program_ids || [],
      max_uses: code.max_uses?.toString() || "",
      max_uses_per_user: code.max_uses_per_user?.toString() || "1",
      min_purchase_amount: code.min_purchase_amount?.toString() || "",
      expiration_date: code.expiration_date
        ? code.expiration_date.split("T")[0]
        : "",
      description: code.description || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCode(null);
    setFormData({
      code: "",
      discount_type: "percentage",
      discount_value: "",
      program_ids: [],
      max_uses: "",
      max_uses_per_user: "1",
      min_purchase_amount: "",
      expiration_date: "",
      description: "",
    });
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard!");
  };

  const toggleProgramSelection = (programId) => {
    setFormData((prev) => ({
      ...prev,
      program_ids: prev.program_ids.includes(programId)
        ? prev.program_ids.filter((id) => id !== programId)
        : [...prev.program_ids, programId],
    }));
  };

  // Filter codes - org's own codes + platform codes if enabled
  const filteredCodes = promoCodes.filter((code) => {
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !code.code.toLowerCase().includes(query) &&
        !code.description?.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    // Filter platform codes
    if (code.scope === "platform" && !showPlatformCodes) {
      return false;
    }

    return true;
  });

  // Separate org codes and platform codes
  const orgCodes = filteredCodes.filter((c) => c.scope === "org");
  const platformCodes = filteredCodes.filter((c) => c.scope === "platform");

  const getProgramNames = (programIds) => {
    if (!programIds || programIds.length === 0) return "All Programs";
    const names = programIds
      .map((id) => programs.find((p) => p.program_id === id)?.name)
      .filter(Boolean);
    return names.length > 0 ? names.join(", ") : "All Programs";
  };

  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageCode, setUsageCode] = useState(null);

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? "min-h-64" : "min-h-screen"}`} role="status" aria-label="Loading promotion codes">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (loadError) {
    const errorState = (
      <section className="mx-auto max-w-xl rounded-2xl border border-rose-200 bg-rose-50 p-6" role="alert">
        <h1 className="text-lg font-semibold text-rose-950">Promotion codes unavailable</h1>
        <p className="mt-2 text-sm leading-6 text-rose-800">{loadError}</p>
        <Button onClick={fetchPromoCodes} className="mt-4 rounded-xl bg-rose-700 hover:bg-rose-800">Try again</Button>
      </section>
    );
    return embedded ? errorState : <AdminLayout>{errorState}</AdminLayout>;
  }

  const handleViewUsage = async (code) => {
    setUsageCode(code);
    setUsageDialogOpen(true);
    setUsageLoading(true);
    setUsageData(null);
    try {
      const res = await axios.get(`${API}/promo-codes/${code.code_id}/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsageData(res.data);
    } catch (error) {
      toast.error('Failed to load usage data');
      setUsageData({ usage_log: [], summary: { total_uses: code.current_uses || 0, total_discount: 0, most_recent_use: null } });
    } finally {
      setUsageLoading(false);
    }
  };

  return (
    <PageLayout>
    <div data-testid="admin-promo-codes">
      {/* Header */}
      <div className="mb-6">
        {!embedded && <Link
          to="/admin"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Dashboard
        </Link>}
        <EditorialPageHeader
          eyebrow="Finance"
          title="Promotion Codes"
          description="Create discount codes for your programs"
          headingId="promotion-codes-heading"
          aside={<Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="create-promo-code-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Promo Code
          </Button>}
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search codes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="showPlatform"
            checked={showPlatformCodes}
            onCheckedChange={setShowPlatformCodes}
          />
          <label htmlFor="showPlatform" className="text-sm text-slate-600 cursor-pointer">
            Show platform-wide codes
          </label>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <Ticket className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{orgCodes.length}</p>
              <p className="text-sm text-slate-500">Your Codes</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {orgCodes.filter((c) => c.is_active).length}
              </p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {orgCodes.reduce((acc, c) => acc + (c.current_uses || 0), 0)}
              </p>
              <p className="text-sm text-slate-500">Total Uses</p>
            </div>
          </div>
        </div>
      </div>

      {/* Your Promo Codes */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="font-semibold text-slate-900">Your Promo Codes</h2>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Code</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Discount</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Programs</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Usage</th>
              <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Status</th>
              <th className="text-right px-6 py-3 text-sm font-medium text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgCodes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-12 text-slate-500">
                  <Ticket className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No promo codes yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      resetForm();
                      setDialogOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create Your First Code
                  </Button>
                </td>
              </tr>
            ) : (
              orgCodes.map((code) => (
                <tr
                  key={code.code_id}
                  className="border-b border-slate-100 hover:bg-slate-50"
                  data-testid={`promo-code-row-${code.code_id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-slate-900 bg-slate-100 px-2 py-1 rounded">
                        {code.code}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                        onClick={() => copyCode(code.code)}
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {code.description && (
                      <p className="text-xs text-slate-500 mt-1">{code.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {code.discount_type === "percentage" ? (
                        <>
                          <Percent className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-green-600">
                            {code.discount_value}% off
                          </span>
                        </>
                      ) : (
                        <>
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-green-600">
                            ${code.discount_value} off
                          </span>
                        </>
                      )}
                    </div>
                    {code.min_purchase_amount && (
                      <p className="text-xs text-slate-500">
                        Min: ${code.min_purchase_amount}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-slate-600 truncate block max-w-[150px]">
                      {getProgramNames(code.program_ids)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-slate-900 font-medium">
                      {code.current_uses || 0}
                      {code.max_uses && ` / ${code.max_uses}`}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {code.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        <XCircle className="w-3 h-3" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(code)}>
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleToggleActive(code)}>
                          {code.is_active ? (
                            <>
                              <XCircle className="w-4 h-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleViewUsage(code)}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Usage
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDelete(code.code_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Platform-Wide Codes (Read Only) */}
      {showPlatformCodes && platformCodes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-purple-50">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-purple-600" />
              <h2 className="font-semibold text-purple-900">Platform-Wide Codes</h2>
            </div>
            <p className="text-sm text-purple-600 mt-1">
              These codes are available for all organizations and work with your programs too.
            </p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Code</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Discount</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Expires</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-slate-500">Status</th>
              </tr>
            </thead>
            <tbody>
              {platformCodes.map((code) => (
                <tr
                  key={code.code_id}
                  className="border-b border-slate-100"
                >
                  <td className="px-6 py-4">
                    <span className="font-mono font-semibold text-slate-900 bg-purple-100 px-2 py-1 rounded">
                      {code.code}
                    </span>
                    {code.description && (
                      <p className="text-xs text-slate-500 mt-1">{code.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {code.discount_type === "percentage" ? (
                        <span className="font-semibold text-purple-600">
                          {code.discount_value}% off
                        </span>
                      ) : (
                        <span className="font-semibold text-purple-600">
                          ${code.discount_value} off
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {code.expiration_date ? (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(code.expiration_date).toLocaleDateString()}
                      </div>
                    ) : (
                      <span className="text-slate-400">No expiry</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {code.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Usage Analytics Dialog */}
      <Dialog open={usageDialogOpen} onOpenChange={setUsageDialogOpen}>
        <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              Usage Analytics {usageCode && <Badge variant="secondary" className="font-mono">{usageCode.code}</Badge>}
            </DialogTitle>
            <DialogDescription>
              View detailed usage history for this promo code
            </DialogDescription>
          </DialogHeader>

          {usageLoading ? (
            <div className="flex items-center justify-center py-12">
              <UsageLoader className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : usageData ? (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{usageData.summary?.total_uses || 0}</p>
                  <p className="text-sm text-slate-500">Total Uses</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">
                    ${(usageData.summary?.total_discount || 0).toFixed(2)}
                  </p>
                  <p className="text-sm text-slate-500">Total Discount Given</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                  <p className="text-sm font-semibold text-slate-900">
                    {usageData.summary?.most_recent_use
                      ? new Date(usageData.summary.most_recent_use).toLocaleDateString()
                      : 'Never'}
                  </p>
                  <p className="text-sm text-slate-500">Most Recent Use</p>
                </div>
              </div>

              {/* Usage Log Table */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Usage Log</h3>
                {(usageData.usage_log || []).length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Ticket className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p>No usage records yet</p>
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left px-4 py-2 text-xs font-medium text-slate-500">Date</th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-slate-500">User</th>
                          <th className="text-left px-4 py-2 text-xs font-medium text-slate-500">Program</th>
                          <th className="text-right px-4 py-2 text-xs font-medium text-slate-500">Discount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usageData.usage_log.map((entry, i) => (
                          <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {new Date(entry.used_at || entry.date).toLocaleDateString()}
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-slate-900">{entry.user_name || entry.email || 'Unknown'}</p>
                              {entry.email && entry.user_name && (
                                <p className="text-xs text-slate-500">{entry.email}</p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-600">
                              {entry.program_name || entry.registration_name || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                              ${(entry.discount_amount || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setUsageDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? "Edit Promo Code" : "Create Promo Code"}
            </DialogTitle>
            <DialogDescription>
              {editingCode
                ? "Update the promo code settings"
                : "Create a discount code for your programs"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Code */}
            <div>
              <Label>Promo Code *</Label>
              <Input
                placeholder="e.g., EARLYBIRD"
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
                className="mt-1 font-mono"
                disabled={!!editingCode}
              />
              <p className="text-xs text-slate-500 mt-1">
                Code will be stored in uppercase
              </p>
            </div>

            {/* Discount Type & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Discount Type *</Label>
                <Select
                  value={formData.discount_type}
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>
                  Discount Value *{" "}
                  {formData.discount_type === "percentage" ? "(%)" : "($)"}
                </Label>
                <Input
                  type="number"
                  placeholder={formData.discount_type === "percentage" ? "20" : "10.00"}
                  value={formData.discount_value}
                  onChange={(e) =>
                    setFormData({ ...formData, discount_value: e.target.value })
                  }
                  className="mt-1"
                  min="0"
                  max={formData.discount_type === "percentage" ? "100" : undefined}
                  step={formData.discount_type === "percentage" ? "1" : "0.01"}
                />
              </div>
            </div>

            {/* Program Restriction */}
            <div>
              <Label>Restrict to Specific Programs</Label>
              <p className="text-xs text-slate-500 mb-2">
                Leave unchecked for all programs
              </p>
              <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                {programs.length === 0 ? (
                  <p className="text-sm text-slate-400 p-2">No programs available</p>
                ) : (
                  programs.map((program) => (
                    <div
                      key={program.program_id}
                      className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                      onClick={() => toggleProgramSelection(program.program_id)}
                    >
                      <Checkbox
                        checked={formData.program_ids.includes(program.program_id)}
                        onCheckedChange={() => toggleProgramSelection(program.program_id)}
                      />
                      <span className="text-sm">{program.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Max Total Uses</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={formData.max_uses}
                  onChange={(e) =>
                    setFormData({ ...formData, max_uses: e.target.value })
                  }
                  className="mt-1"
                  min="0"
                />
              </div>
              <div>
                <Label>Max Uses Per User</Label>
                <Input
                  type="number"
                  placeholder="1"
                  value={formData.max_uses_per_user}
                  onChange={(e) =>
                    setFormData({ ...formData, max_uses_per_user: e.target.value })
                  }
                  className="mt-1"
                  min="1"
                />
              </div>
            </div>

            {/* Min Purchase & Expiration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Minimum Purchase ($)</Label>
                <Input
                  type="number"
                  placeholder="No minimum"
                  value={formData.min_purchase_amount}
                  onChange={(e) =>
                    setFormData({ ...formData, min_purchase_amount: e.target.value })
                  }
                  className="mt-1"
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Expiration Date</Label>
                <Input
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) =>
                    setFormData({ ...formData, expiration_date: e.target.value })
                  }
                  className="mt-1"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Description (Internal Note)</Label>
              <Input
                placeholder="e.g., Early bird discount"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {editingCode ? "Update Code" : "Create Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </PageLayout>
  );
};

export default AdminPromoCodes;
